/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import ArgosFlexbox from '../../../../../../../components/Containers/ArgosFlexbox';
import ArgosMenuItem from '../../../../../../../components/Form/Select/ArgosMenuItem';
import ArgosSelect from '../../../../../../../components/Form/Select/ArgosSelect';
import { fetchModelsEndpoint, fetchModelsError, fetchVariationsEndpoint, fetchVariationsError, 
  fetchVersionsEndpoint, fetchVersionsError } from './constants';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import React, { useEffect, useState } from 'react';
import { renderModelNameString, renderVersionString } from '../../../../../../../components/Text/TextFormatting';
import toast from 'react-hot-toast';
import { usePostRequest } from '../../../../../../../../api/helper';


interface SelectModelProps {
  designOfExperimentData: any
  handleChange: any
  disableSelect: boolean
}

interface FetchOptionsResponse {
  values: string[]
}

const SelectModel = (props: SelectModelProps) => {
  const { designOfExperimentData, handleChange, disableSelect } = props

  const [selectedModel, setSelectedModel] = useState<string>(designOfExperimentData?.name ?? '')
  const [selectedVersion, setSelectedVersion] = useState<string>(designOfExperimentData?.version ?? '')
  const [selectedVariation, setSelectedVariation] = useState<string>(designOfExperimentData?.variation ?? '')

  const [models, setModels] = useState<string[]>([])
  const [versions, setVersions] = useState<string[]>([])
  const [variations, setVariations] = useState<string[]>([])

  const [isLoadingModels, setIsLoadingModels] = useState<boolean>(false)
  const [isLoadingVersions, setIsLoadingVersions] = useState<boolean>(false)
  const [isLoadingVariations, setIsLoadingVariations] = useState<boolean>(false)

  const fetchModelsHandle = usePostRequest(fetchModelsEndpoint)
  const fetchModels = () => {
    setIsLoadingModels(true)
    fetchModelsHandle().then((resp: FetchOptionsResponse) => {
      setModels(resp.values)
    }).catch((e) => {
      console.log(e)
      toast.error(fetchModelsError)
    }).finally(() => {
      setIsLoadingModels(false)
    })
  }

  const fetchVersionsHandle = usePostRequest(fetchVersionsEndpoint)
  const fetchVersions = (model: string) => {
    setIsLoadingVersions(true)
    const req = {
      'model': model
    }
    fetchVersionsHandle(req).then((resp: FetchOptionsResponse) => {
      setVersions(resp.values)
    }).catch((e) => {
      toast.error(fetchVersionsError)
      console.log(e)
    }).finally(() => {
      setIsLoadingVersions(false)
    })
  }

  const fetchVariationsHandle = usePostRequest(fetchVariationsEndpoint)
  const fetchVariations = (model: string, version: string) => {
    setIsLoadingVariations(true)
    const req = {
      'model': model,
      'version': version
    }
    fetchVariationsHandle(req).then((resp: FetchOptionsResponse) => {
      setVariations(resp.values)
    }).catch((e) => {
      toast.error(fetchVariationsError)
      console.log(e)
    }).finally(() => {
      setIsLoadingVariations(false)
    })
  }

  useEffect(() => {
    setSelectedModel(designOfExperimentData?.name ?? '')
    setSelectedVersion(designOfExperimentData?.version ?? '')
    setSelectedVariation(designOfExperimentData?.variation ?? '')
  }, [designOfExperimentData])

  useEffect(() => {
    fetchModels()
  }, [])

  useEffect(() => {
    if (selectedModel !== '') {
      fetchVersions(selectedModel)
    }
  }, [selectedModel])

  useEffect(() => {
    if (selectedModel !== '' && selectedVersion !== '') {
      fetchVariations(selectedModel, selectedVersion)
    }
  }, [selectedVersion])

  return (
    <ArgosFlexbox style={{flex: 1, flexDirection: 'row', padding: '10px'}}>
      <FormControl variant='standard'>
        <InputLabel id='model'>Model</InputLabel>
        <ArgosSelect
          id='model'
          label='Model'
          type='text'
          value={selectedModel}
          onChange={(e) => handleChange('model', e.target.value)}
          disabled={isLoadingModels || disableSelect}
        >
          {models.map((model: string, index: number) =>
            <ArgosMenuItem key={index} value={model}>
              {renderModelNameString(model)}
            </ArgosMenuItem>
          )}
        </ArgosSelect>
      </FormControl>

      <FormControl variant='standard'>
        <InputLabel id='version'>Version</InputLabel>
        <ArgosSelect
          id='version'
          label='Version'
          type='text'
          value={selectedVersion}
          onChange={(e) => handleChange('version', e.target.value)}
          disabled={selectedModel === '' || isLoadingVersions || disableSelect}
        >
          {versions.map((version: string, index: number) => 
            <ArgosMenuItem key={index} value={version}>
              {renderVersionString(version)}
            </ArgosMenuItem>
          )}
        </ArgosSelect>
      </FormControl>

      <FormControl variant='standard' style={{flex: 1}}>
        <InputLabel id='variation'>Variation</InputLabel>
        <ArgosSelect
          id='variation'
          label='Variation'
          type='text'
          value={selectedVariation}
          onChange={(e) => handleChange('variation', e.target.value)}
          disabled={selectedVersion === '' || isLoadingVariations || disableSelect}
        >
          {variations.map((variation: string, index: number) =>
            <ArgosMenuItem key={index} value={variation}>
              {variation}
            </ArgosMenuItem>
          )}
        </ArgosSelect>
      </FormControl>
    </ArgosFlexbox>
  )
}

export default SelectModel
